import { StreamId } from "@common/domain";
import {
  EVENT,
  MIXER_PIPE_TYPE,
  MIXER_RENDER_TYPE,
  StreamKindOrigin
} from "avcore";
import { Map, Set } from "immutable";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  asyncScheduler,
  combineLatest,
  defer
} from "rxjs";
import {
  catchError,
  concatMap,
  debounceTime,
  mergeMap,
  observeOn,
  pairwise,
  startWith,
  takeUntil,
  timeout
} from "rxjs/operators";
import { RtmpId } from "../actions";
import {
  MixerAnyCmd,
  MixerAudioStreamAddCmd,
  MixerAudioStreamDeleteCmd,
  MixerBgDeleteCmd,
  MixerBgSetCmd,
  MixerCaptionLowerThirdDeleteCmd,
  MixerCaptionLowerThirdSetCmd,
  MixerLogoDeleteCmd,
  MixerLogoSetCmd,
  MixerOverlayDeleteCmd,
  MixerOverlaySetCmd,
  MixerPipeStopCmd,
  MixerRecordingPipeStartCmd,
  MixerStreamDeleteCmd,
  MixerVideoStreamAddCmd,
  MixerVideoStreamDeleteCmd
} from "../domain/command";
import {
  MixerConfig,
  MixerId,
  MixerPipeId,
  MixerStreamId
} from "../domain/interface";
import {
  MixerErrorVO,
  MixerExitVO,
  MixerStreamStartedVO,
  MixerStreamStoppedVO,
  MixerVideoStreamVO
} from "../domain/valueobject";
import { assert, toDiv } from "../utils";
import { MixerInvalidRtmpPipeError } from "./error";
import {
  FullScreenLayout,
  GridLayout,
  Layout,
  SidebarLayout,
  StreamBadge
} from "./layout";
import { MixerApi } from "./MixerApi";
import { StreamBadgeFactory } from "./StreamBadgeFactory";
import { StreamBgUrlFactory } from "./StreamBgUrlFactory";

export class Mixer {
  private static WIDTH = 1_280;
  private static HEIGHT = 720;
  private static ASPECT = Mixer.WIDTH / Mixer.HEIGHT;
  // should be divisible by 8 (avcore)
  private static STREAM_MARGIN = 8;
  private static CAPTION_MAX_HEIGHT = 400;
  private static CAPTION_WIDTH_MARGIN = 100;
  private static CAPTION_MAX_WIDTH =
    Mixer.WIDTH - 2 * Mixer.CAPTION_WIDTH_MARGIN;
  private static CAPTION_HEIGHT_MARGIN =
    Mixer.CAPTION_WIDTH_MARGIN / Mixer.ASPECT;
  private static LOGO_MAX_HEIGHT = 100;
  private static LOGO_MAX_WIDTH = 100;
  private static LOGO_WIDTH_MARGIN_RIGHT = 50;
  private static LOGO_HEIGHT_MARGIN_TOP = 50;
  private static LOGO_WIDTH_MARGIN_LEFT =
    Mixer.WIDTH - Mixer.LOGO_MAX_WIDTH - Mixer.LOGO_WIDTH_MARGIN_RIGHT;
  private static INVISIBLE_OPTIONS = {
    x: 8,
    y: 8,
    width: 8,
    height: 8,
    z: -1
  };
  private static MIXER_UPDATE_DEBOUNCE_TIME_MS = 100;
  // Mixer layout limited to 9 streams
  private static VIDEO_STREAMS_CAPACITY = 9;

  private readonly destroy$ = new Subject<void>();
  private readonly layout$ = new BehaviorSubject<Layout>(
    new FullScreenLayout({
      width: Mixer.WIDTH,
      height: Mixer.HEIGHT,
      streamMargin: Mixer.STREAM_MARGIN
    })
  );
  private readonly layoutVideoStreams$ = new BehaviorSubject<StreamId[]>([]);
  private readonly layoutAudioStreams$ = new BehaviorSubject<StreamId[]>([]);
  private readonly mixerApi: MixerApi;
  private readonly mixerCommand$ = new Subject<MixerAnyCmd>();
  private readonly _mixerError$ = new Subject<MixerErrorVO>();
  private readonly _mixerExit$ = new Subject<MixerExitVO>();
  private _mixerId: null | MixerId = null;
  private readonly _mixerStreamStarted$ = new Subject<MixerStreamStartedVO>();
  private readonly _mixerStreamStopped$ = new Subject<MixerStreamStoppedVO>();
  private readonly _streamIds$ = new BehaviorSubject<StreamIds>(Set());
  private readonly _videoStreams$ = new BehaviorSubject<VideoStreams>(Map());
  private readonly _audioStreams$ = new BehaviorSubject<AudioStreams>(Set());

  constructor(
    readonly streamId: StreamId,
    readonly mixerConfig: MixerConfig,
    private readonly streamBadgeFactory: StreamBadgeFactory,
    private readonly streamBgUrlFactory: StreamBgUrlFactory
  ) {
    this.mixerApi = new MixerApi(
      this.mixerConfig.url,
      this.mixerConfig.worker,
      this.mixerConfig.token
    );

    combineLatest([this._videoStreams$, this.layoutVideoStreams$, this.layout$])
      .pipe(
        debounceTime(Mixer.MIXER_UPDATE_DEBOUNCE_TIME_MS),
        concatMap((_) =>
          defer(() => this.onLayoutVideoStreamsUpdate(_[0], _[1], _[2])).pipe(
            catchError(this.onMixerError)
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();

    combineLatest([
      this._audioStreams$,
      this.layoutAudioStreams$.pipe(startWith([]), pairwise())
    ])
      .pipe(
        debounceTime(Mixer.MIXER_UPDATE_DEBOUNCE_TIME_MS),
        mergeMap((_) =>
          this.onLayoutAudioStreamsUpdate(_[0], _[1][0], _[1][1])
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.mixerCommand$
      .pipe(
        observeOn(asyncScheduler),
        concatMap((_) =>
          defer(() => this.onMixerCommand(_)).pipe(
            timeout(15_000),
            catchError(this.onMixerError)
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  static async mixerRecordingPipeStart(
    mixerRecordingPipeStartCmd: MixerRecordingPipeStartCmd
  ): Promise<MixerPipeId> {
    const { mixerConfig, mixerId, prefix } = mixerRecordingPipeStartCmd.value;

    const api = new MixerApi(
      mixerConfig.url,
      mixerConfig.worker,
      mixerConfig.token
    );

    try {
      const { pipeId } = await api.mixerPipeStart({
        mixerId,
        type: MIXER_PIPE_TYPE.RECORDING,
        kinds: ["audio", "video"],
        // TODO: hot fix
        // @ts-expect-error pass the prefix for the recording file
        prefix
      });

      return pipeId as MixerPipeId;
    } finally {
      api.clear();
    }
  }

  static async mixerPipeStop(mixerPipeStopCmd: MixerPipeStopCmd) {
    const { mixerConfig, mixerId, mixerPipeId } = mixerPipeStopCmd.value;

    const api = new MixerApi(
      mixerConfig.url,
      mixerConfig.worker,
      mixerConfig.token
    );

    try {
      await api.mixerPipeStop({
        mixerId,
        pipeId: mixerPipeId
      });
    } finally {
      api.clear();
    }
  }

  get audioStreams$(): Observable<AudioStreams> {
    return this._audioStreams$.asObservable().pipe(observeOn(asyncScheduler));
  }

  get mixerError$(): Observable<MixerErrorVO> {
    return this._mixerError$.asObservable().pipe(observeOn(asyncScheduler));
  }

  get mixerExit$(): Observable<MixerExitVO> {
    return this._mixerExit$.asObservable().pipe(observeOn(asyncScheduler));
  }

  get mixerId(): null | MixerId {
    return this._mixerId;
  }

  get mixerStreamStarted$(): Observable<MixerStreamStartedVO> {
    return this._mixerStreamStarted$
      .asObservable()
      .pipe(observeOn(asyncScheduler));
  }

  get mixerStreamStopped$(): Observable<MixerStreamStoppedVO> {
    return this._mixerStreamStopped$
      .asObservable()
      .pipe(observeOn(asyncScheduler));
  }

  get streamIds$(): Observable<StreamIds> {
    return this._streamIds$.asObservable().pipe(observeOn(asyncScheduler));
  }

  get videoStreams$(): Observable<VideoStreams> {
    return this._videoStreams$.asObservable().pipe(observeOn(asyncScheduler));
  }

  async clean(): Promise<void> {
    for (const streamId of this._streamIds$.value) {
      await this.mixerStreamDelete1(streamId);
    }

    await this.mixerBgDelete1();
    await this.mixerCaptionLowerThirdDelete1();
    await this.mixerLogoDelete1();
    await this.mixerOverlayDelete1();
  }

  async start(): Promise<void> {
    // MIXER_FILE_STOPPED would be dispatched right after an image was added to the stream
    // (because ffmpeg process would be down right after the image frame was grabbed to the MCU stream).
    // Don't use it to understand whether a file stream exists on an MCU stream or not.

    assert(!this._mixerId, "mixerId already set");

    this.mixerApi.client.on("error", this.onMixerError);
    this.mixerApi.client.on("disconnect", this.onMixerDisconnect);
    this.mixerApi.client.on(EVENT.STREAM_STARTED, this.onStreamStarted);
    this.mixerApi.client.on(EVENT.STREAM_STOPPED, this.onStreamStopped);
    this.mixerApi.client.on(EVENT.MIXER_STOPPED, this.onMixerStopped);

    const { mixerId } = await this.mixerApi.mixerStart({
      width: Mixer.WIDTH,
      height: Mixer.HEIGHT
    });

    this._mixerId = mixerId as MixerId;

    try {
      await this.mixerApi.listenMixerStopped({ mixerId: this._mixerId });

      await this.mixerApi.mixerPipeStart({
        stream: this.streamId,
        mixerId: this._mixerId,
        type: MIXER_PIPE_TYPE.LIVE,
        kinds: ["audio", "video"]
      });
    } catch (error) {
      // https://liveedev.atlassian.net/browse/YOUM-80
      await this.mixerApi.mixerClose({ mixerId: this._mixerId }).catch();

      throw error;
    }
  }

  async stop(): Promise<void> {
    this.destroy$.next();
    this.destroy$.complete();

    this.layout$.complete();
    this.layoutVideoStreams$.complete();
    this.layoutAudioStreams$.complete();
    this.mixerCommand$.complete();
    this._mixerError$.complete();
    this._mixerExit$.complete();
    this._mixerStreamStarted$.complete();
    this._mixerStreamStopped$.complete();
    this._streamIds$.complete();
    this._videoStreams$.complete();
    this._audioStreams$.complete();

    this.mixerApi.client.removeListener("error", this.onMixerError);
    this.mixerApi.client.removeListener("disconnect", this.onMixerDisconnect);
    this.mixerApi.client.removeListener(
      EVENT.STREAM_STARTED,
      this.onStreamStarted
    );
    this.mixerApi.client.removeListener(
      EVENT.STREAM_STOPPED,
      this.onStreamStopped
    );
    this.mixerApi.client.removeListener(
      EVENT.MIXER_STOPPED,
      this.onMixerStopped
    );

    if (this._mixerId) {
      await this.mixerApi.mixerClose({ mixerId: this._mixerId });
    }

    this.mixerApi.clear();
    this._mixerId = null;
  }

  setFullScreenLayout(): void {
    this.layout$.next(
      new FullScreenLayout({
        width: Mixer.WIDTH,
        height: Mixer.HEIGHT,
        streamMargin: Mixer.STREAM_MARGIN
      })
    );
  }

  setGridLayout(): void {
    this.layout$.next(
      new GridLayout({
        width: Mixer.WIDTH,
        height: Mixer.HEIGHT,
        streamMargin: Mixer.STREAM_MARGIN
      })
    );
  }

  setLayoutVideoStreams(streams: StreamId[]): void {
    this.layoutVideoStreams$.next(streams);
  }

  setLayoutAudioStreams(streams: StreamId[]): void {
    this.layoutAudioStreams$.next(streams);
  }

  setSidebarLayout(): void {
    this.layout$.next(
      new SidebarLayout({
        width: Mixer.WIDTH,
        height: Mixer.HEIGHT,
        streamMargin: Mixer.STREAM_MARGIN
      })
    );
  }

  mixerAudioStreamAdd(mixerAudioStreamAddCmd: MixerAudioStreamAddCmd): void {
    this.mixerCommand$.next(mixerAudioStreamAddCmd);
  }

  mixerAudioStreamDelete(
    mixerAudioStreamDeleteCmd: MixerAudioStreamDeleteCmd
  ): void {
    this.mixerCommand$.next(mixerAudioStreamDeleteCmd);
  }

  mixerBgDelete(): void {
    this.mixerCommand$.next(new MixerBgDeleteCmd());
  }

  mixerBgSet(mixerBgSetCmd: MixerBgSetCmd): void {
    this.mixerCommand$.next(mixerBgSetCmd);
  }

  mixerCaptionLowerThirdDelete(): void {
    this.mixerCommand$.next(new MixerCaptionLowerThirdDeleteCmd());
  }

  mixerCaptionLowerThirdSet(
    mixerCaptionLowerThirdSetCmd: MixerCaptionLowerThirdSetCmd
  ): void {
    this.mixerCommand$.next(mixerCaptionLowerThirdSetCmd);
  }

  mixerLogoDelete(): void {
    this.mixerCommand$.next(new MixerLogoDeleteCmd());
  }

  mixerLogoSet(mixerLogoSetCmd: MixerLogoSetCmd): void {
    this.mixerCommand$.next(mixerLogoSetCmd);
  }

  mixerOverlayDelete(): void {
    this.mixerCommand$.next(new MixerOverlayDeleteCmd());
  }

  mixerOverlaySet(mixerOverlaySetCmd: MixerOverlaySetCmd): void {
    this.mixerCommand$.next(mixerOverlaySetCmd);
  }

  mixerStreamDelete(mixerStreamDeleteCmd: MixerStreamDeleteCmd): void {
    this.mixerCommand$.next(mixerStreamDeleteCmd);
  }

  mixerVideoStreamAdd(mixerVideoStreamAddCmd: MixerVideoStreamAddCmd): void {
    this.mixerCommand$.next(mixerVideoStreamAddCmd);
  }

  mixerVideoStreamDelete(
    mixerVideoStreamDeleteCmd: MixerVideoStreamDeleteCmd
  ): void {
    this.mixerCommand$.next(mixerVideoStreamDeleteCmd);
  }

  private async mixerAudioStreamAdd1(
    mixerAudioStreamAddCmd: MixerAudioStreamAddCmd
  ): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const { streamId } = mixerAudioStreamAddCmd.value;

    this._streamIds$.next(this._streamIds$.value.add(streamId));

    // Remove stream data in order to avoid duplication
    await this.mixerApi.mixerRemove({
      stream: streamId,
      mixerId: this._mixerId,
      kind: "audio"
    });

    await this.mixerApi.listenStreamStopped({
      stream: streamId,
      kind: "audio"
    });
    await this.mixerApi.listenStreamStarted({
      stream: streamId,
      kind: "audio"
    });

    await this.mixerApi.mixerAdd({
      options: Mixer.INVISIBLE_OPTIONS,
      stream: streamId,
      mixerId: this._mixerId,
      kind: "audio"
    });

    this._audioStreams$.next(this._audioStreams$.value.add(streamId));
  }

  private async mixerAudioStreamDelete1(streamId: StreamId): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi.mixerRemove({
      stream: streamId,
      mixerId: this._mixerId,
      kind: "audio"
    });

    this._audioStreams$.next(this._audioStreams$.value.delete(streamId));
  }

  private async mixerBgDelete1(): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.bgStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
  }

  private async mixerBgSet1(mixerBgSetCmd: MixerBgSetCmd): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const { bgUrl } = mixerBgSetCmd.value;

    await this.mixerApi.mixerRemove({
      stream: Mixer.bgStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
    await this.mixerApi.mixerAddFile({
      options: {
        x: 0,
        y: 0,
        width: Mixer.WIDTH,
        height: Mixer.HEIGHT,
        z: Mixer.bgZ,
        renderType: MIXER_RENDER_TYPE.SCALE
      },
      stream: Mixer.bgStreamId(this.streamId),
      kinds: ["video"],
      mixerId: this._mixerId,
      filePath: bgUrl,
      hasTransparency: true,
      removeOnExit: false
    });
  }

  private async mixerCaptionLowerThirdDelete1(): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.captionLowerThirdStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
  }

  private async mixerCaptionLowerThirdSet1(
    mixerCaptionLowerThirdSetCmd: MixerCaptionLowerThirdSetCmd
  ): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const { captionLowerThirdUrl } = mixerCaptionLowerThirdSetCmd.value;
    let { captionLowerThirdHeight, captionLowerThirdWidth } =
      mixerCaptionLowerThirdSetCmd.value;

    const aspect = captionLowerThirdWidth / captionLowerThirdHeight;
    if (captionLowerThirdWidth > Mixer.CAPTION_MAX_WIDTH) {
      captionLowerThirdWidth = Mixer.CAPTION_MAX_WIDTH;
      captionLowerThirdHeight = captionLowerThirdWidth / aspect;
    } else if (captionLowerThirdHeight > Mixer.CAPTION_MAX_HEIGHT) {
      captionLowerThirdHeight = Mixer.CAPTION_MAX_HEIGHT;
      captionLowerThirdWidth = captionLowerThirdHeight * aspect;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.captionLowerThirdStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
    await this.mixerApi.mixerAddFile({
      options: {
        x: toDiv(Mixer.CAPTION_WIDTH_MARGIN, 2),
        y: toDiv(
          Mixer.HEIGHT - captionLowerThirdHeight - Mixer.CAPTION_HEIGHT_MARGIN,
          2
        ),
        width: toDiv(captionLowerThirdWidth, 8),
        height: toDiv(captionLowerThirdHeight, 8),
        z: Mixer.captionZ,
        renderType: MIXER_RENDER_TYPE.SCALE
      },
      stream: Mixer.captionLowerThirdStreamId(this.streamId),
      kinds: ["video"],
      mixerId: this._mixerId,
      filePath: captionLowerThirdUrl,
      hasTransparency: true,
      removeOnExit: false
    });
  }

  private async mixerLogoDelete1(): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.logoStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
  }

  private async mixerLogoSet1(mixerLogoSetCmd: MixerLogoSetCmd): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const { logoUrl } = mixerLogoSetCmd.value;
    let { logoWidth, logoHeight } = mixerLogoSetCmd.value;

    const aspect = logoWidth / logoHeight;
    if (logoWidth > Mixer.LOGO_MAX_WIDTH) {
      logoWidth = Mixer.LOGO_MAX_WIDTH;
      logoHeight = logoWidth / aspect;
    } else if (logoHeight > Mixer.LOGO_MAX_HEIGHT) {
      logoHeight = Mixer.LOGO_MAX_HEIGHT;
      logoWidth = logoHeight * aspect;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.logoStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
    await this.mixerApi.mixerAddFile({
      options: {
        x: toDiv(Mixer.LOGO_WIDTH_MARGIN_LEFT, 2),
        y: toDiv(Mixer.LOGO_HEIGHT_MARGIN_TOP, 2),
        width: toDiv(logoWidth, 8, true),
        height: toDiv(logoHeight, 8, true),
        z: Mixer.logoZ,
        renderType: MIXER_RENDER_TYPE.SCALE
      },
      stream: Mixer.logoStreamId(this.streamId),
      kinds: ["video"],
      mixerId: this._mixerId,
      filePath: logoUrl,
      hasTransparency: true,
      removeOnExit: false
    });
  }

  private async mixerOverlayDelete1(): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.overlayStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
  }

  private async mixerOverlaySet1(
    mixerOverlaySetCmd: MixerOverlaySetCmd
  ): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const { overlayUrl } = mixerOverlaySetCmd.value;
    let { overlayWidth, overlayHeight } = mixerOverlaySetCmd.value;

    const aspect = overlayWidth / overlayHeight;

    overlayHeight = Mixer.HEIGHT;
    overlayWidth = overlayHeight * aspect;

    if (overlayWidth > Mixer.WIDTH) {
      overlayWidth = Mixer.WIDTH;
      overlayHeight = overlayWidth / aspect;
    }

    await this.mixerApi.mixerRemove({
      stream: Mixer.overlayStreamId(this.streamId),
      mixerId: this._mixerId,
      kind: "video"
    });
    await this.mixerApi.mixerAddFile({
      options: {
        x: toDiv((Mixer.WIDTH - overlayWidth) / 2, 2),
        y: toDiv((Mixer.HEIGHT - overlayHeight) / 2, 2),
        width: overlayWidth,
        height: overlayHeight,
        z: Mixer.overlayZ,
        renderType: MIXER_RENDER_TYPE.SCALE
      },
      stream: Mixer.overlayStreamId(this.streamId),
      kinds: ["video"],
      mixerId: this._mixerId,
      filePath: overlayUrl,
      hasTransparency: true,
      removeOnExit: false
    });
  }

  private async mixerStreamDelete1(streamId: StreamId): Promise<void> {
    await this.mixerVideoStreamDelete1(streamId);
    await this.mixerAudioStreamDelete1(streamId);
  }

  private async mixerVideoStreamAdd1(
    mixerVideoStreamAddCmd: MixerVideoStreamAddCmd
  ): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const { streamId, streamName } = mixerVideoStreamAddCmd.value;
    const streamBadgeStreamId = Mixer.streamBadgeStreamId(streamId);

    const streamBadge = this.streamBadgeFactory.streamBadgeCreate({
      title: streamName,
      height: StreamBadge.STREAM_BADGE_HEIGHT,
      maxWidth: StreamBadge.STREAM_BADGE_MAX_WIDTH
    });

    this._streamIds$.next(this._streamIds$.value.add(streamId));

    // Remove stream data in order to avoid duplication
    await this.mixerApi.mixerRemove({
      stream: streamBadgeStreamId,
      mixerId: this._mixerId,
      kind: "video"
    });
    await this.mixerApi.mixerRemove({
      stream: streamId,
      mixerId: this._mixerId,
      kind: "video"
    });

    await this.mixerApi.listenStreamStopped({
      stream: streamId,
      kind: "video"
    });
    await this.mixerApi.listenStreamStarted({
      stream: streamId,
      kind: "video"
    });

    await this.mixerApi!.mixerAddFile({
      options: Mixer.INVISIBLE_OPTIONS,
      stream: streamBadgeStreamId,
      kinds: ["video"],
      mixerId: this._mixerId,
      filePath: streamBadge.url,
      hasTransparency: true,
      removeOnExit: false
    });
    await this.mixerApi.mixerAdd({
      stream: streamId,
      options: Mixer.INVISIBLE_OPTIONS,
      mixerId: this._mixerId,
      kind: "video"
    });

    this._videoStreams$.next(
      this._videoStreams$.value.set(
        streamId,
        new MixerVideoStreamVO({ streamId, streamName })
      )
    );
  }

  private async mixerVideoStreamDelete1(streamId: StreamId): Promise<void> {
    if (!this._mixerId) {
      return;
    }

    const streamBadgeStreamId = Mixer.streamBadgeStreamId(streamId);

    await this.mixerApi.mixerRemove({
      stream: streamBadgeStreamId,
      mixerId: this._mixerId,
      kind: "video"
    });
    await this.mixerApi.mixerRemove({
      stream: streamId,
      mixerId: this._mixerId,
      kind: "video"
    });

    this._audioStreams$.next(this._audioStreams$.value.delete(streamId));
    this._videoStreams$.next(this._videoStreams$.value.delete(streamId));
    this._streamIds$.next(this._streamIds$.value.delete(streamId));
  }

  private async onMixerCommand(mixerAnyCmd: MixerAnyCmd) {
    if (mixerAnyCmd instanceof MixerVideoStreamAddCmd) {
      return this.mixerVideoStreamAdd1(mixerAnyCmd);
    } else if (mixerAnyCmd instanceof MixerAudioStreamAddCmd) {
      return this.mixerAudioStreamAdd1(mixerAnyCmd);
    } else if (mixerAnyCmd instanceof MixerStreamDeleteCmd) {
      return this.mixerStreamDelete1(mixerAnyCmd.streamId);
    } else if (mixerAnyCmd instanceof MixerBgDeleteCmd) {
      return this.mixerBgDelete1();
    } else if (mixerAnyCmd instanceof MixerBgSetCmd) {
      return this.mixerBgSet1(mixerAnyCmd);
    } else if (mixerAnyCmd instanceof MixerCaptionLowerThirdDeleteCmd) {
      return this.mixerCaptionLowerThirdDelete1();
    } else if (mixerAnyCmd instanceof MixerCaptionLowerThirdSetCmd) {
      return this.mixerCaptionLowerThirdSet1(mixerAnyCmd);
    } else if (mixerAnyCmd instanceof MixerLogoDeleteCmd) {
      return this.mixerLogoDelete1();
    } else if (mixerAnyCmd instanceof MixerLogoSetCmd) {
      return this.mixerLogoSet1(mixerAnyCmd);
    } else if (mixerAnyCmd instanceof MixerOverlayDeleteCmd) {
      return this.mixerOverlayDelete1();
    } else if (mixerAnyCmd instanceof MixerOverlaySetCmd) {
      return this.mixerOverlaySet1(mixerAnyCmd);
    } else if (mixerAnyCmd instanceof MixerAudioStreamDeleteCmd) {
      return this.mixerAudioStreamDelete1(mixerAnyCmd.streamId);
    } else if (mixerAnyCmd instanceof MixerVideoStreamDeleteCmd) {
      return this.mixerVideoStreamDelete1(mixerAnyCmd.streamId);
    }

    assert(false, "Invalid mixerAnyCmd");
  }

  /**
   * @deprecated Should not be used.
   */
  async updateStreamsOrder(_streams: UpdateStreamOrder[]) {
    return;
  }

  static async stopMixer(
    mixerId: MixerId,
    mixerConfig: MixerConfig
  ): Promise<void> {
    const api = new MixerApi(
      mixerConfig.url,
      mixerConfig.worker,
      mixerConfig.token
    );

    try {
      await api.mixerClose({ mixerId });
    } finally {
      api.clear();
    }
  }

  static async startRtmp(data: StartRtmpReq): Promise<MixerPipeId> {
    const { mixerConfig, mixerId, key, url } = data;
    const api = new MixerApi(
      mixerConfig.url,
      mixerConfig.worker,
      mixerConfig.token
    );

    try {
      const { pipeId } = await api.mixerPipeStart({
        url: `${url}/${key}`,
        mixerId: mixerId,
        type: MIXER_PIPE_TYPE.RTMP,
        kinds: ["audio", "video"]
      });

      if (!pipeId) {
        throw new MixerInvalidRtmpPipeError(mixerId);
      }

      return pipeId as MixerPipeId;
    } finally {
      api.clear();
    }
  }

  static async stopRtmp(data: StopRtmpReq): Promise<void> {
    const { mixerConfig, rtmpId, mixerId } = data;
    const api = new MixerApi(
      mixerConfig.url,
      mixerConfig.worker,
      mixerConfig.token
    );

    try {
      await api.mixerPipeStop({
        pipeId: rtmpId,
        mixerId: mixerId
      });
    } finally {
      api.clear();
    }
  }

  private static streamBadgeStreamId(streamId: StreamId) {
    return `${streamId}_stream_badge`;
  }

  private static streamBgStreamId(streamId: StreamId) {
    return `${streamId}_stream_bg`;
  }

  private static isStreamBadgeStreamId(streamId: StreamId) {
    return streamId.endsWith("_stream_badge");
  }

  private static captionLowerThirdStreamId(streamId: StreamId) {
    return `${streamId}_captionLowerThird`;
  }

  private static logoStreamId(streamId: StreamId) {
    return `${streamId}_logo`;
  }

  private static bgStreamId(streamId: StreamId) {
    return `${streamId}_bg`;
  }

  private static overlayStreamId(streamId: StreamId) {
    return `${streamId}_overlay`;
  }

  private static get bgZ() {
    return 1;
  }

  private static get streamBgZ() {
    return 2;
  }

  private static get streamZ() {
    // Keep it in sync with a mixer logic.
    return 3;
  }

  private static get streamBadgeZ() {
    return 4;
  }

  private static get overlayZ() {
    return 5;
  }

  private static get captionZ() {
    return 6;
  }

  private static get logoZ() {
    return 6;
  }

  private async renderVideoStream(
    layout: Layout,
    mixerVideoStreamVO: MixerVideoStreamVO,
    videoStreamIndex: StreamIndex,
    videoStreamsCount: number
  ) {
    if (!this._mixerId) {
      return;
    }

    const { streamId } = mixerVideoStreamVO.value;

    const streamCoordinates = layout.streamCoordinatesGet({
      streamIndex: videoStreamIndex,
      streamsCount: videoStreamsCount
    });

    await this.mixerApi!.mixerUpdate({
      stream: streamId,
      kind: "video",
      options: {
        x: streamCoordinates.x,
        y: streamCoordinates.y,
        width: streamCoordinates.width,
        height: streamCoordinates.height,
        z: Mixer.streamZ,
        renderType: MIXER_RENDER_TYPE.PAD
      },
      mixerId: this._mixerId
    });
  }

  private async renderAudioStream(stream: StreamId) {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi!.mixerUpdate({
      stream,
      kind: "audio",
      options: {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        z: 1
      },
      mixerId: this._mixerId
    });
  }

  private async renderVideoStreamBadge(
    layout: Layout,
    mixerVideoStreamVO: MixerVideoStreamVO,
    videoStreamIndex: StreamIndex,
    videoStreamsCount: number
  ) {
    if (!this._mixerId) {
      return;
    }

    const { streamId, streamName } = mixerVideoStreamVO.value;

    const streamCoordinates = layout.streamCoordinatesGet({
      streamIndex: videoStreamIndex,
      streamsCount: videoStreamsCount
    });

    const streamBadgeStreamId = Mixer.streamBadgeStreamId(streamId);
    const streamBadgeHeight = StreamBadge.streamBadgeHeight(streamCoordinates);

    const streamBadge = this.streamBadgeFactory.streamBadgeCreate({
      title: streamName,
      height: streamBadgeHeight,
      maxWidth: streamCoordinates.width
    });

    const streamBadgeHeight1 = toDiv(streamBadge.height, 8);
    const streamBadgeWidth1 = toDiv(streamBadge.width, 8);

    await this.mixerApi!.mixerUpdate({
      options: {
        x: streamCoordinates.x,
        y:
          streamCoordinates.y + (streamCoordinates.height - streamBadgeHeight1),
        width: streamBadgeWidth1,
        height: streamBadgeHeight1,
        z: Mixer.streamBadgeZ
      },
      stream: streamBadgeStreamId,
      kind: "video",
      mixerId: this._mixerId
    });
  }

  private async hideVideoStream(mixerVideoStreamVO: MixerVideoStreamVO) {
    if (!this._mixerId) {
      return;
    }

    const { streamId } = mixerVideoStreamVO.value;

    await this.mixerApi!.mixerUpdate({
      stream: streamId,
      kind: "video",
      options: Mixer.INVISIBLE_OPTIONS,
      mixerId: this._mixerId
    });
  }

  private async hideAudioStream(streamId: StreamId) {
    if (!this._mixerId) {
      return;
    }

    await this.mixerApi!.mixerUpdate({
      stream: streamId,
      kind: "audio",
      options: Mixer.INVISIBLE_OPTIONS,
      mixerId: this._mixerId
    });
  }

  private async hideVideoStreamBadge(mixerVideoStreamVO: MixerVideoStreamVO) {
    if (!this._mixerId) {
      return;
    }

    const { streamId } = mixerVideoStreamVO.value;
    const streamBadgeStreamId = Mixer.streamBadgeStreamId(streamId);

    await this.mixerApi!.mixerUpdate({
      stream: streamBadgeStreamId,
      kind: "video",
      options: Mixer.INVISIBLE_OPTIONS,
      mixerId: this._mixerId
    });
  }

  private async onLayoutVideoStreamsUpdate(
    videoStreams: VideoStreams,
    videoStreamsOnLayout: StreamId[],
    layout: Layout
  ) {
    videoStreamsOnLayout = videoStreamsOnLayout.slice(
      0,
      Mixer.VIDEO_STREAMS_CAPACITY
    );

    // To make a layout update smoother:
    // 1. Hide all badges.
    // 2. Hide all streams.
    // 3. Render streams in new positions.
    // 4. Render badges.

    for (const [_streamId, videoStream] of videoStreams) {
      await this.hideVideoStreamBadge(videoStream);
      await this.hideVideoStream(videoStream);
    }

    // Iterate over videoStreamsOnLayout to preserve ordering
    for (const [index, streamId] of [...videoStreamsOnLayout].entries()) {
      const videoStream = videoStreams.get(streamId);
      if (!videoStream) {
        // Video stream to draw does not exist
        // Just ignore that, they can appear after
        continue;
      }

      await this.renderVideoStream(
        layout,
        videoStream,
        index,
        videoStreamsOnLayout.length
      );
      await this.renderVideoStreamBadge(
        layout,
        videoStream,
        index,
        videoStreamsOnLayout.length
      );
    }
  }

  private async onLayoutAudioStreamsUpdate(
    streams: AudioStreams,
    prevLayoutStreams: StreamId[],
    nextLayoutStreams: StreamId[]
  ) {
    const streamsSet = Set(streams);
    const prevSet = Set(prevLayoutStreams);
    const nextSet = Set(nextLayoutStreams);

    const streamsToHide = prevSet.subtract(nextSet).intersect(streamsSet);
    const streamsToAdd = nextSet.intersect(streamsSet);

    for (const stream of streamsToHide) {
      await this.hideAudioStream(stream);
    }

    for (const stream of streamsToAdd) {
      await this.renderAudioStream(stream);
    }
  }

  private onMixerDisconnect = () => {
    if (!this._mixerId) {
      return;
    }

    this.onMixerError(
      new Error(
        `Mixer ${this._mixerId} for stream ${this.streamId} disconnected`
      )
    );
  };

  private onMixerError = (error: Error) => {
    if (!this._mixerId) {
      return EMPTY;
    }

    const mixerId = this._mixerId;
    this._mixerId = null;

    this._mixerError$.next(
      new MixerErrorVO({ error, streamId: this.streamId, mixerId })
    );

    return EMPTY;
  };

  private onMixerStopped = () => {
    if (!this._mixerId) {
      return;
    }

    const mixerId = this._mixerId;
    this._mixerId = null;

    this._mixerExit$.next(
      new MixerExitVO({ streamId: this.streamId, mixerId })
    );
  };

  private onStreamStarted = (data: StreamKindOrigin) => {
    if (!this._mixerId) {
      return;
    }

    this._mixerStreamStarted$.next(
      new MixerStreamStartedVO({
        mixerId: this._mixerId,
        streamId: this.streamId,
        data: {
          streamId: data.stream as MixerStreamId,
          kind: data.kind
        }
      })
    );
  };

  private onStreamStopped = (data: StreamKindOrigin) => {
    if (!this._mixerId) {
      return;
    }

    this._mixerStreamStopped$.next(
      new MixerStreamStoppedVO({
        mixerId: this._mixerId,
        streamId: this.streamId,
        data: {
          streamId: data.stream as MixerStreamId,
          kind: data.kind
        }
      })
    );
  };
}

type StreamIndex = number;
type UpdateStreamOrder = [StreamId, StreamIndex];
type AudioStreams = Set<StreamId>;
type StreamIds = Set<StreamId>;
type VideoStreams = Map<StreamId, MixerVideoStreamVO>;

interface StartRtmpReq {
  key: string;
  mixerId: MixerId;
  mixerConfig: MixerConfig;
  url: string;
}

interface StopRtmpReq {
  mixerId: MixerId;
  mixerConfig: MixerConfig;
  rtmpId: RtmpId;
}
